import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

export interface ActionEntry {
    id: string;
    label: string;
    Icon: () => React.JSX.Element;
    colorClass: string;
    requiresMessage: boolean;
    allowsAttachment: boolean;
    submit: (message: string, file?: File) => Promise<boolean>;
}

interface ActionDropdownProps {
    disabled?: boolean;
    actionConfig: ActionEntry[];
    activeActionId: string;
    onActionChange: (actionId: string) => void;
}

export function ActionDropdown({disabled, actionConfig, activeActionId, onActionChange}: ActionDropdownProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const {t} = useTranslation();
    const activeAction = actionConfig.find((action) => action.id === activeActionId) ?? actionConfig[0];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (activeAction == null) return null;

    return (
        <div className="relative" ref={dropdownRef} id="action-dropdown-container">
            <button
                id="action-change-dropdown-button"
                type="button"
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm transition-colors hover:bg-gray-700"
                disabled={disabled}
                onClick={() => setIsDropdownOpen((open) => !open)}
            >
                <activeAction.Icon/>
                <span>{activeAction.label}</span>
            </button>
            {isDropdownOpen && (
                <div className="absolute bottom-full left-0 mb-2 z-10 min-w-[180px] rounded-xl border border-gray-600 bg-gray-really py-1 shadow-xl">
                    <div className="px-1">
                        {actionConfig.map(({id, label, Icon, colorClass}) => (
                            <button
                                key={id}
                                type="button"
                                title={t(`${label} action`)}
                                className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-gray-700 text-nowrap rounded-xl ${colorClass}`}
                                onClick={() => {
                                    onActionChange(id);
                                    setIsDropdownOpen(false);
                                }}
                            >
                                <div className="flex flex-row gap-2 items-center" id={`action-dropdown-option-${id}`}>
                                    <Icon/>
                                    <span>{label}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
